import React, { useEffect, useState, createContext, useContext } from "react";
import { AuthUser, loginWithCredentials, reAuthWithToken, getStoredToken, getStoredUser, persistLawvoSession, establishDcSession, clearStoredAuth, fetchDcSession } from "../data/auth";
import { getDcAccessToken, saveDcState } from "../data/lawvoStorage";
export const PREVIEW_AUTH_TOKEN = "mp-preview-token";
const PREVIEW_USER: AuthUser = {
  id: 0,
  email: "preview@lawvo.dev",
  firstName: "Preview",
  lastName: "User",
  role: "super-admin"
};
interface AuthContextType {
  user: AuthUser | null;
  accessToken: string | null;
  dcJwt: string | null;
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  isDcAuthenticated: boolean;
  isPreviewSession: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginAsPreview: () => void;
  logout: () => void;
  refreshDcSession: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
async function bootstrapDcSession(user: AuthUser, forceRefresh = false): Promise<string | null> {
  try {
    const session = await establishDcSession(user, {
      forceRefresh
    });
    return session.jwt;
  } catch (error) {
    console.warn("Failed to establish Document Center session:", error);
    return getDcAccessToken() || null;
  }
}
export const AuthProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser());
  const [accessToken, setAccessToken] = useState<string | null>(getStoredToken());
  const [dcJwt, setDcJwt] = useState<string | null>(getDcAccessToken() || null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  useEffect(() => {
    const validateToken = async () => {
      const token = getStoredToken();
      if (!token) {
        setIsAuthLoading(false);
        return;
      }
      if (token === PREVIEW_AUTH_TOKEN) {
        setUser(getStoredUser() ?? PREVIEW_USER);
        setAccessToken(PREVIEW_AUTH_TOKEN);
        setIsAuthLoading(false);
        return;
      }
      try {
        const response = await reAuthWithToken(token);
        if (response.user.role !== "super-admin") {
          console.error("User is not a super-admin, denying access");
          clearStoredAuth();
          setUser(null);
          setAccessToken(null);
          setDcJwt(null);
          return;
        }
        persistLawvoSession(response);
        setUser(response.user);
        setAccessToken(response.accessToken);
        const jwt = await bootstrapDcSession(response.user, false);
        setDcJwt(jwt);
      } catch (error) {
        console.error("Token validation failed:", error);
        clearStoredAuth();
        setUser(null);
        setAccessToken(null);
        setDcJwt(null);
      } finally {
        setIsAuthLoading(false);
      }
    };
    validateToken();
  }, []);
  const refreshDcSession = async () => {
    if (!user || user.id === 0) return;
    const session = await fetchDcSession(user);
    saveDcState({
      user: session.user,
      jwt: session.jwt
    });
    setDcJwt(session.jwt);
  };
  const loginAsPreview = () => {
    setUser(PREVIEW_USER);
    setAccessToken(PREVIEW_AUTH_TOKEN);
    setDcJwt(null);
  };
  const login = async (email: string, password: string) => {
    const response = await loginWithCredentials(email, password);
    if ("requires2FA" in response && response.requires2FA) {
      throw new Error("Two-factor authentication is required. Please complete 2FA in the main LawVo application first.");
    }
    if ("accessToken" in response && "user" in response) {
      if (response.user.role !== "super-admin") {
        throw new Error("Access denied. Only super-admin users can access the Translation Workspace.");
      }
      persistLawvoSession(response);
      setUser(response.user);
      setAccessToken(response.accessToken);
      const jwt = await bootstrapDcSession(response.user, true);
      if (!jwt) {
        throw new Error("Signed in to Lawvo but could not obtain a Document Center session. Check Strapi connectivity.");
      }
      setDcJwt(jwt);
    }
  };
  const logout = () => {
    clearStoredAuth();
    setUser(null);
    setAccessToken(null);
    setDcJwt(null);
  };
  return <AuthContext.Provider value={{
    user,
    accessToken,
    dcJwt,
    isAuthLoading,
    isAuthenticated: !!accessToken,
    isDcAuthenticated: !!dcJwt,
    isPreviewSession: accessToken === PREVIEW_AUTH_TOKEN,
    login,
    loginAsPreview,
    logout,
    refreshDcSession
  }} data-id="element-241">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};